import { FC, useEffect, useMemo, useState } from 'react';
import { AxiosError } from 'axios';
import dayjs from 'dayjs';
import { Autocomplete, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, InputAdornment, TextField, Typography, useMediaQuery, useTheme } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import BadgeOutlinedIcon from '@mui/icons-material/BadgeOutlined';
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined';
import SaveIcon from '@mui/icons-material/Save';
import Landing from '../../../../Models/LandingModels/Landing';
import ShipCaptain from '../../../../Models/ShipModels/ShipCaptain';
import { getShipCaptains, patchLanding } from '../../../../Services/LondunarkerfiAPIService';
import { formatCaptainNationalId, getCaptainNationalIdDigits, isCaptainNationalIdValid } from '../../../../Helpers/CaptainInfo';
import { logError } from '../../../../Helpers/LogError';

interface CaptainInfoRequirementProps {
  landing: Landing;
  onSaved: (captainInfo: { captainName: string; captainNationalId: string }) => void;
  showSnackbar: (message: string, severity: string) => void;
  readOnly: boolean;
}

const CaptainInfoRequirement: FC<CaptainInfoRequirementProps> = (props) => {
  const [captainName, setCaptainName] = useState('');
  const [captainNationalId, setCaptainNationalId] = useState('');
  const [registeredCaptains, setRegisteredCaptains] = useState<ShipCaptain[]>([]);
  const [captainsLoading, setCaptainsLoading] = useState(false);
  const [captainsLoadFailed, setCaptainsLoadFailed] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [manualDialogOpen, setManualDialogOpen] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  useEffect(() => {
    setCaptainName(props.landing.captainName || '');
    setCaptainNationalId(formatCaptainNationalId(props.landing.captainNationalId));
    setSubmitted(false);
  }, [props.landing.id, props.landing.captainName, props.landing.captainNationalId]);

  useEffect(() => {
    let isActive = true;

    const fetchRegisteredCaptains = async () => {
      const shipRegistrationNumber = props.landing.ship?.shipRegistrationNumber;
      if (!shipRegistrationNumber || props.landing.ship?.isForeign) {
        setRegisteredCaptains([]);
        setCaptainsLoadFailed(false);
        setCaptainsLoading(false);
        return;
      }

      const landingDate = dayjs(props.landing.landingStarts);
      const lookupDate = landingDate.isValid() ? landingDate : dayjs();
      const dateFrom = lookupDate.subtract(30, 'day').format('YYYY-MM-DD');
      const dateTo = lookupDate.add(1, 'day').format('YYYY-MM-DD');

      try {
        setCaptainsLoading(true);
        setCaptainsLoadFailed(false);
        const captains = await getShipCaptains(shipRegistrationNumber, dateFrom, dateTo);
        if (isActive) {
          setRegisteredCaptains(captains);
        }
      } catch (error) {
        if (isActive) {
          setRegisteredCaptains([]);
          setCaptainsLoadFailed(true);
        }
        logError(error);
      } finally {
        if (isActive) {
          setCaptainsLoading(false);
        }
      }
    };

    fetchRegisteredCaptains();

    return () => {
      isActive = false;
    };
  }, [props.landing.id, props.landing.landingStarts, props.landing.ship?.isForeign, props.landing.ship?.shipRegistrationNumber]);

  const captainNameIsMissing = submitted && !captainName.trim();
  const captainNationalIdIsInvalid = submitted && !isCaptainNationalIdValid(captainNationalId);

  const selectedRegisteredCaptain = useMemo(() => {
    const nationalIdDigits = getCaptainNationalIdDigits(captainNationalId);
    if (!nationalIdDigits) {
      return null;
    }

    return registeredCaptains.find(captain => getCaptainNationalIdDigits(captain.nationalId) === nationalIdDigits) || null;
  }, [captainNationalId, registeredCaptains]);

  const formIsValid = useMemo(() => {
    return captainName.trim().length > 0 && isCaptainNationalIdValid(captainNationalId);
  }, [captainName, captainNationalId]);

  const handleCaptainNationalIdChange = (value: string) => {
    const digits = getCaptainNationalIdDigits(value).slice(0, 10);
    setCaptainNationalId(formatCaptainNationalId(digits));
  };

  const handleRegisteredCaptainChange = (captain: ShipCaptain | null) => {
    if (!captain) {
      return;
    }

    setCaptainName(captain.name || '');
    setCaptainNationalId(formatCaptainNationalId(captain.nationalId));
    setSubmitted(false);
  };

  const handleSave = async () => {
    setSubmitted(true);
    if (!formIsValid || props.readOnly) {
      return;
    }

    try {
      setLoading(true);
      const captainInfo = {
        captainName: captainName.trim(),
        captainNationalId
      };
      const response = await patchLanding(props.landing.id, {
        captainName: captainInfo.captainName,
        captainNationalId: captainInfo.captainNationalId
      });
      props.showSnackbar(response?.status === 204 ? 'Upplýsingar skipstjóra voru þegar skráðar.' : 'Upplýsingar skipstjóra vistaðar.', 'success');
      setManualDialogOpen(false);
      props.onSaved(captainInfo);
    } catch (error: unknown) {
      const axiosError = error as AxiosError;
      if (axiosError.response?.data && typeof axiosError.response.data !== 'object') {
        props.showSnackbar(String(axiosError.response.data), 'error');
      } else {
        logError(error);
        props.showSnackbar('Óvænt villa kom upp!', 'error');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      data-testid="captain-info-requirement"
      sx={{
        display: 'flex',
        flexDirection: isMobile ? 'column' : 'row',
        gap: 1,
        alignItems: isMobile ? 'stretch' : 'center',
        p: 1,
        borderRadius: 1,
        border: '1px solid',
        borderColor: theme.palette.mode === 'dark' ? 'rgba(245, 158, 11, 0.45)' : 'rgba(217, 119, 6, 0.35)',
        borderLeft: '4px solid',
        borderLeftColor: theme.palette.warning.main,
        backgroundColor: theme.palette.mode === 'dark' ? 'rgba(245, 158, 11, 0.10)' : 'rgba(255, 247, 237, 0.95)',
      }}
    >
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', minWidth: isMobile ? 0 : 220 }}>
        <ReportProblemOutlinedIcon color="warning" fontSize="small" />
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontWeight: 700, lineHeight: 1.2 }}>
            Vantar upplýsingar um skipstjóra
          </Typography>
          {props.readOnly && (
            <Typography variant="caption" color="error" sx={{ display: 'block', mt: 1 }}>
              Þú hefur ekki réttindi til að skrá þessar upplýsingar.
            </Typography>
          )}
        </Box>
      </Box>

      <Autocomplete
        options={registeredCaptains}
        loading={captainsLoading}
        loadingText="Sæki..."
        noOptionsText={captainsLoadFailed ? 'Ekki tókst að sækja skráða skipstjóra' : 'Enginn skráður skipstjóri fannst'}
        autoHighlight
        value={selectedRegisteredCaptain}
        onChange={(event, value) => handleRegisteredCaptainChange(value)}
        isOptionEqualToValue={(option, value) => getCaptainNationalIdDigits(option.nationalId) === getCaptainNationalIdDigits(value.nationalId)}
        getOptionLabel={(option) => `${option.name} - ${formatCaptainNationalId(option.nationalId)}`}
        disabled={loading || props.readOnly}
        sx={{ flex: 1, minWidth: isMobile ? 0 : 280 }}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Skráðir skipstjórar"
            size="small"
            InputProps={{
              ...params.InputProps,
              startAdornment: (
                <>
                  <InputAdornment position="start">
                    <BadgeOutlinedIcon fontSize="small" />
                  </InputAdornment>
                  {params.InputProps.startAdornment}
                </>
              ),
              endAdornment: (
                <>
                  {captainsLoading ? <CircularProgress color="inherit" size={18} /> : null}
                  {params.InputProps.endAdornment}
                </>
              )
            }}
          />
        )}
      />
      <LoadingButton
        loading={loading && !manualDialogOpen}
        disabled={loading || props.readOnly || !formIsValid}
        onClick={handleSave}
        variant="contained"
        color="warning"
        startIcon={<SaveIcon />}
        sx={{ minHeight: '40px', whiteSpace: 'nowrap', borderRadius: 1 }}
      >
        Vista
      </LoadingButton>
      <Button
        color="warning"
        disabled={loading || props.readOnly}
        onClick={() => setManualDialogOpen(true)}
        size="small"
        sx={{ whiteSpace: 'nowrap' }}
      >
        Skrá handvirkt
      </Button>

      <Dialog
        open={manualDialogOpen}
        onClose={() => setManualDialogOpen(false)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Skrá skipstjóra handvirkt</DialogTitle>
        <DialogContent sx={{ display: 'grid', gap: 1, pt: '0.5em !important' }}>
          <TextField
            label="Nafn skipstjóra"
            value={captainName}
            onChange={(event) => setCaptainName(event.target.value)}
            error={captainNameIsMissing}
            helperText={captainNameIsMissing ? 'Skráðu nafn skipstjóra.' : ' '}
            disabled={loading || props.readOnly}
            size="small"
            fullWidth
          />
          <TextField
            label="Kennitala skipstjóra"
            value={captainNationalId}
            onChange={(event) => handleCaptainNationalIdChange(event.target.value)}
            onBlur={() => setCaptainNationalId(formatCaptainNationalId(captainNationalId))}
            error={captainNationalIdIsInvalid}
            helperText={captainNationalIdIsInvalid ? 'Kennitala þarf að innihalda 10 tölustafi.' : ' '}
            disabled={loading || props.readOnly}
            size="small"
            fullWidth
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <BadgeOutlinedIcon fontSize="small" />
                </InputAdornment>
              )
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setManualDialogOpen(false)} disabled={loading}>
            Hætta við
          </Button>
          <LoadingButton
            loading={loading && manualDialogOpen}
            disabled={loading || props.readOnly}
            onClick={handleSave}
            variant="contained"
            color="warning"
            startIcon={<SaveIcon />}
            sx={{ minHeight: '40px', whiteSpace: 'nowrap', borderRadius: 1 }}
          >
            Vista
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CaptainInfoRequirement;
